import React from 'react';
import classnames from 'classnames';
import { observer, inject } from 'mobx-react';

import DateTime from '@/components/DateTime';
import Dialogue from '@/components/Dialogue';
import Msg from '@/components/Msg';
import Tip from '@/components/Tip';
import Announcements from '@/components/Announcements';
import HotQuestionCategory from '@/components/HotQuestionCategory';
import { AnnouncementsSkeleton, HotQuestionCategorySkeleton, MsgSkeleton, IntentHotQuestionSkeleton } from '@/components/Skeleton';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { DIALOGUE_TYPE } from '@/types/common';
import FirstPageSkeleton from '../Skeleton/FirstPage';
import { isBank } from '@/config/core';
import { AgentDialogueLoader, DialogueLoader } from '../Common/Loader/firstLoad';

import './style.scss';


const PREFIX = 'session-area-container';

const SESSION_COMPONENTS: Record<string, any> = {
  DateTime,
  Msg,
  Tip,
  Announcements,
  HotQuestionCategory,
  AnnouncementsSkeleton,
  HotQuestionCategorySkeleton,
  MsgSkeleton,
  IntentHotQuestionSkeleton,
};

interface Props {
  firstInit: any;
  userStore: any;
}

function SessionArea(props: Props) {
  const {
    firstInit,
    firstInit: { initStore },
    userStore,
  } = props;

  // 是否已过期
  const hasExpired = (dialogue: any) => {
    return dialogue.sessionId !== initStore.sessionId;
  };

  const dialogueExtraChildrenGenerater = (dialogue: any) => {
    const Component = SESSION_COMPONENTS[dialogue.type + (dialogue.skeleton ? 'Skeleton' : '')];
    if (!Component) {
      return null;
    }

    const isDisabled = dialogue?.data?.isDisabled; // 点过隐藏逻辑（暂时没有）

    switch (dialogue.type) {
      case DIALOGUE_TYPE.OrderList:
      case DIALOGUE_TYPE.StateTransactionList:
      case DIALOGUE_TYPE.Announcements:
      case DIALOGUE_TYPE.HotQuestionCategory:
      case DIALOGUE_TYPE.TFEOrderList:
      case DIALOGUE_TYPE.TFETransactionList:
      case DIALOGUE_TYPE.OrderSelector:
        const componentProps = {
          ...dialogue.data,
          from: dialogue.from,
          historyDialogueId: dialogue.historyDialogueId,
          historySessionId: dialogue.historySessionId,
          dialogueId: dialogue.dialogueId,
          dialogueType: dialogue.dialogueType,
          nodePointId: dialogue.nodePointId,
          listPos: dialogue.dialogueIndex,
          isDisabled: isDisabled,
          isHistory: dialogue.isHistory || false,
          taskbotName: dialogue.taskbotName,
          kbBotId: dialogue.kbBotId || dialogue.data.kbBotId,
          tfNodeId: dialogue.tfNodeId,
          tfCaseId: dialogue.tfCaseId,
        };
        return <Component {...componentProps} />;
      default:
        return null;
    }
  };

  const dialogueChildrenGenerater = (dialogue: any) => {
    const { isHistory = false } = dialogue;
    const { userId } = userStore;

    const Component = SESSION_COMPONENTS[dialogue.type + (dialogue.skeleton ? 'Skeleton' : '')];

    if (!Component) {
      return null;
    }

    // 在旧逻辑中，isDisabled用于隐藏button
    // 历史消息中，添加disabledBtn用于禁止点击button
    let isDisabled = false;
    const disabledBtn = isHistory;
    switch (dialogue.type) {
      case DIALOGUE_TYPE.Live: // Live: 点过隐藏
        isDisabled = dialogue.data.isDisabled && !isHistory;
        break;
      case DIALOGUE_TYPE.Options: // Options: session过期的会全部隐藏
        isDisabled = hasExpired(dialogue) && !isHistory;
        break;
      case DIALOGUE_TYPE.QAButton: // QAButton: session过期的会全部隐藏
        isDisabled = hasExpired(dialogue) && !isHistory;
        break;
      case DIALOGUE_TYPE.Feedback: // Feedback: 点过隐藏 & session过期的会全部隐藏
        isDisabled = (dialogue.data.isDisabled || hasExpired(dialogue)) && !isHistory;
        break;
      case DIALOGUE_TYPE.Login: // Login: 已登录的会隐藏Login按钮
        isDisabled = !!userId && !isHistory;
        break;
      case DIALOGUE_TYPE.EnquiryDetails: // EnquiryDetails: 点过隐藏
        isDisabled = dialogue.data.isDisabled && !isHistory;
        break;
      case DIALOGUE_TYPE.NegativeOptions: // NegativeOptions: 点过隐藏 & session过期的会全部隐藏
        isDisabled = (dialogue.data.isDisabled || hasExpired(dialogue)) && !isHistory;
        break;
    }

    const data = { ...dialogue.data };
    if (dialogue.type === DIALOGUE_TYPE.General) {
      data.options = (dialogue.data.options || []).map((option: any) => {
        // 在旧逻辑中，isBtnDisabled用于隐藏button
        // 历史消息中，添加disabledBtn用于禁止点击button
        let isBtnDisabled = false;
        switch (option.button_type) {
          case 'btn_taskbot':
            isBtnDisabled = hasExpired(dialogue) && !isHistory;
            break;
          case 'btn_live':
            isBtnDisabled = dialogue.data.isDisabled && !isHistory;
            break;
          case 'btn_cancel_order':
            isBtnDisabled = dialogue.data.isCancelOrderDisabled && !isHistory;
            break;
          case 'btn_activate_shopeepay':
            isBtnDisabled = dialogue.data.isActivateShopeepayDisabled && !isHistory;
            break;
          case 'btn_redirection':
            if (option.uri_extra && option.uri_key === 'request_order_return_page') {
              isBtnDisabled = dialogue.data.isActivateReturnRefundDisabled && !isHistory;
            }
            break;
          case 'btn_more_order':
            isBtnDisabled = hasExpired(dialogue) && !isHistory;
            break;
        }
        return {
          ...option,
          isBtnDisabled,
          disabledBtn,
          isHistory,
        };
      });
    }

    if (dialogue.type === DIALOGUE_TYPE.Options) {
      data.options = (dialogue.data.options || []).map((option: any) => ({ ...option, disabledBtn, isHistory }));
    }

    switch (dialogue.type) {
      case DIALOGUE_TYPE.OrderList:
      case DIALOGUE_TYPE.StateTransactionList:
      case DIALOGUE_TYPE.TFEOrderList:
      case DIALOGUE_TYPE.TFETransactionList:
      case DIALOGUE_TYPE.OrderSelector:
        return null;
      default:
        const componentProps = {
          ...data,
          historyDialogueId: dialogue.historyDialogueId,
          historySessionId: dialogue.historySessionId,
          dialogueId: dialogue.dialogueId,
          dialogueType: dialogue.dialogueType,
          nodePointId: dialogue.nodePointId,
          isDisabled: isDisabled,
          listPos: dialogue.dialogueIndex,
          from: dialogue.from,
          isHistory: dialogue.isHistory || false,
          disabledBtn,
          taskbotName: dialogue.taskbotName,
          kbBotId: dialogue.kbBotId || data.kbBotId,
          tfNodeId: dialogue.tfNodeId,
          tfCaseId: dialogue.tfCaseId,
        };
        return <Component {...componentProps} />;
    }
  };

  const sessionDialogueContent = (dialogue: any, key: any, needHideBubbleAngle = false, isFisrtTime = false) => {
    const Component = SESSION_COMPONENTS[dialogue.type + (dialogue.skeleton ? 'Skeleton' : '')];

    if (!Component) {
      return null;
    }
    let dialogueContent = null;

    if (dialogue.from === 'system') {
      // 首屏第一条时间(firstDateTime)被announcements挡住，上拉获取历史消息or关闭 announcements 再展示
      // 对 ios 系统, datetime 再往下移动一部分以确保 msg 与 announcement 保持距离
      const u = navigator.userAgent;
      const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1;
      const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
      dialogueContent = <Component {...dialogue.data} key={key} isFisrtTime={isFisrtTime} isIOS={!isAndroid && isIOS} hiddenDatetime={false} />;
    } else {
      const renderStyle = [
        DIALOGUE_TYPE.Order,
        DIALOGUE_TYPE.Transaction,
        DIALOGUE_TYPE.Video,
        DIALOGUE_TYPE.Image,
        DIALOGUE_TYPE.HistoryImage,
        DIALOGUE_TYPE.TFEOrder,
        DIALOGUE_TYPE.TFEFoodBuyerOrder,
      ].includes(dialogue.type)
        ? 'reversedBubble'
        : 'bubble';
      const extraChildren = dialogueExtraChildrenGenerater(dialogue);
      const children
        = dialogue.type === DIALOGUE_TYPE.Announcements
        || dialogue.type === DIALOGUE_TYPE.HotQuestionCategory
          ? null
          : dialogueChildrenGenerater(dialogue);
      // Announcements不在消息中显示只固定在顶部
      const minimizeDialogue = [DIALOGUE_TYPE.Announcements].includes(dialogue.type);
      // 除了 message/history-image 以外 给所有 dialogue 一个最小宽度 300px
      const noMinWidthList = [DIALOGUE_TYPE.TFEMessage, DIALOGUE_TYPE.Msg, DIALOGUE_TYPE.HistoryImage, DIALOGUE_TYPE.Video, DIALOGUE_TYPE.TFEOrder, DIALOGUE_TYPE.TFEContactCourierCard];
      const haveMinWidth = !noMinWidthList.includes(dialogue.type);
      // OrderList类型的消息的order列表，session过期的会全部隐藏
      const isDisabled = dialogue.type === DIALOGUE_TYPE.OrderList && hasExpired(dialogue);
      // 在 video upload 需求中 图片显示了angle
      // 需要改变的话 参考下面第二行
      const hideBubbleAngle = needHideBubbleAngle;
      // 填满bubble
      const showMaxWidth = dialogue.type === DIALOGUE_TYPE.IntentHotQuestion || dialogue.showMaxWidth || (dialogue.type === DIALOGUE_TYPE.Msg && dialogue.skeleton);
      // 不使用bubble的max-width
      const dontUseMaxWidth = dialogue.type === DIALOGUE_TYPE.HotQuestionCategory || (dialogue.type === DIALOGUE_TYPE.Announcements && dialogue.skeleton);
      // 使用max-width: 100%
      const useFullMaxWidth = dialogue.type === DIALOGUE_TYPE.TFEOrderList || dialogue.type === DIALOGUE_TYPE.OrderList;

      const dialogueProps: any = {
        key,
        from: dialogue.from,
        renderStyle,
        extraChildren,
        isDisabled,
        hideBubbleAngle,
        minimizeDialogue,
        haveMinWidth,
        dontUseMaxWidth,
        showMaxWidth,
        useFullMaxWidth,
      };

      if (dialogue.feedbackInfo) {
        dialogueProps.feedbackInfo = {
          id: dialogue?.data?.id,
          dialogueId: dialogue.dialogueId,
          dialogueType: dialogue.dialogueType,
          listPos: dialogue.dialogueIndex,
          isHistory: dialogue.isHistory || false,
          nodePointId: dialogue.nodePointId,
          historyDialogueId: dialogue.historyDialogueId,
          historySessionId: dialogue.historySessionId,
          kbBotId: dialogue.kbBotId,
          tfNodeId: dialogue.tfNodeId,
          tfCaseId: dialogue.tfCaseId,
          ...dialogue.feedbackInfo,
        };
      }

      dialogueContent = <Dialogue {...dialogueProps}>{children}</Dialogue>;
    }

    return dialogueContent;
  };

  // dialogue生成器
  const dialogueGenerater = (dialogue: any, index: any, needHideBubbleAngle = false, isFisrtTime = false) => {
    let dialogueContent = null;
    const key = `dialogue-${index}`;
    dialogueContent = sessionDialogueContent(dialogue, key, needHideBubbleAngle, isFisrtTime);

    return dialogueContent;
  };
  // 所有消息中的第一条msg
  const firstMsgIndex = firstInit.dataList.findIndex((dialogue: any) => dialogue.type === DIALOGUE_TYPE.Msg);
  // 根据 form 判断对话内容来源判断,不一致即为单边对话中的第一条 msg(需要展示 BubbleAngle)
  const dialogues = firstInit.dataList.map((dialogue: any, index: number) => dialogueGenerater(dialogue, index, (index > firstMsgIndex && firstInit?.dataList[index - 1]?.from === dialogue?.from) || index < firstMsgIndex, index === 0));
  return (
    <div className={classnames(PREFIX, { 'no-bottom-bar': initStore.shouldBottomBarHidden })}>
      <div className="sessions-container">
        {dialogues}
        {firstInit.showFirstLoadLoading && (
          <Dialogue from="bot">
            {isBank() ? <AgentDialogueLoader /> : <DialogueLoader />}
          </Dialogue>
        )}
        {firstInit.initStore.showSkeleton && <FirstPageSkeleton />}
      </div>
    </div>
  );
}

export default inject(getChildInject('firstInit', 'userStore'))(observer(SessionArea as React.FC<{}>));

